import React, { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, Users, CheckCircle, Printer, Calendar, FileText,
  BarChart3, PieChart as PieChartIcon, Activity, UserCheck, Briefcase, MapPin,
  Clock
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { eventAPI, analyticsAPI, attendeeAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';
import { useBadgeSettings } from '../context/BadgeSettingsContext';
import { useEvent } from '../context/EventContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import PieChartCard from '../components/analytics/PieChartCard';
import BarChartCard from '../components/analytics/BarChartCard';

const Analytics = () => {
  const toast = useToastContext();
  const navigate = useNavigate();
  const { defaultPrintMode } = useBadgeSettings();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const chartsRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const response = await eventAPI.getAll();
      return response.data;
    },
  });

  // Fetch comprehensive analytics
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return null;
      const response = await analyticsAPI.getDashboard(selectedEvent);
      return response.data;
    },
    enabled: !!selectedEvent,
  });

  // Fetch attendees for charts only
  const { data: attendeesData } = useQuery({
    queryKey: ['attendees', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return null;
      const response = await attendeeAPI.getAll(selectedEvent);
      return response.data;
    },
    enabled: !!selectedEvent,
  });

  // Fetch field statistics for dynamic analytics
  const { data: fieldStatsData, isLoading: fieldStatsLoading } = useQuery({
    queryKey: ['field-statistics', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return null;
      const response = await analyticsAPI.getFieldStatistics(selectedEvent);
      return response.data;
    },
    enabled: !!selectedEvent,
  });

  // Auto-select first event
  React.useEffect(() => {
    if (eventsData?.events?.length > 0 && !selectedEvent) {
      setSelectedEvent(eventsData.events[0].id);
    }
  }, [eventsData, selectedEvent, setSelectedEvent]);

  const events = eventsData?.events || [];
  const stats = analyticsData?.summary || {};

  // Memoize demographics to prevent dependency issues
  const demographics = useMemo(() => analyticsData?.demographics || {}, [analyticsData]);
  const geographic = useMemo(() => analyticsData?.geographic || {}, [analyticsData]);
  const eventDetails = useMemo(() => analyticsData?.event || {}, [analyticsData]);

  // Memoize data to prevent dependency issues
  const timeAnalytics = useMemo(() => analyticsData?.timeAnalytics || {}, [analyticsData]);
  const attendeeCategories = useMemo(() => analyticsData?.attendeeCategories || {}, [analyticsData]);
  const registrationTrend = useMemo(() => analyticsData?.registrationTrend || [], [analyticsData]);
  const attendees = useMemo(() => attendeesData?.attendees || [], [attendeesData]);

  // Process chart data from API response
  const chartData = useMemo(() => {
    const checkInStatus = [
      { name: 'Checked In', value: attendeeCategories.checkedIn || 0, color: '#10B981' },
      { name: 'Pending', value: attendeeCategories.pending || 0, color: '#EF4444' },
    ].filter(item => item.value > 0);

    return {
      registrationTrend: registrationTrend || [],
      checkInStatus,
      hourlyDistribution: timeAnalytics.hourly || [],
      weeklyDistribution: timeAnalytics.weekly || [],
      monthlyDistribution: timeAnalytics.monthly || [],
    };
  }, [attendeeCategories, registrationTrend, timeAnalytics]);

  // Process field statistics for charts with normalization
  const fieldStatistics = useMemo(() => {
    if (!fieldStatsData || !fieldStatsData.fieldStatistics) {
      return {};
    }

    const rawStats = fieldStatsData.fieldStatistics;
    const normalizedStats = {};

    Object.keys(rawStats).forEach(fieldName => {
      const fieldData = rawStats[fieldName];

      // Apply normalization for country fields or other text fields that need it
      // We check if it's a country field by name (case insensitive)
      const isCountryField = fieldName.toLowerCase().includes('country');

      if (isCountryField && fieldData.distribution && Array.isArray(fieldData.distribution)) {
        const mergedDistribution = {};
        let newTotal = 0;

        fieldData.distribution.forEach(item => {
          // In fieldStatistics: item.value is the LABEL, item.count is the NUMBER
          if (!item.value) return;

          // Normalize: trim whitespace, remove trailing dots, convert to title case for grouping
          // This handles "Ghana ", "ghana", "GHANA", "Ghana."
          let normalizedLabel = item.value.toString().trim().replace(/\.$/, '');

          // Simple title case implementation
          normalizedLabel = normalizedLabel.charAt(0).toUpperCase() + normalizedLabel.slice(1).toLowerCase();

          // Special case for abbreviations like USA, UK if needed
          // If the original was all caps and length <= 3, keep it (e.g. USA, UAE)
          if (item.value.length <= 3 && item.value === item.value.toUpperCase()) {
            normalizedLabel = item.value;
          }
          // Fix for "South Africa" (restore capital A)
          normalizedLabel = normalizedLabel.replace(/\b\w/g, c => c.toUpperCase());

          const count = item.count || 0;

          if (mergedDistribution[normalizedLabel]) {
            mergedDistribution[normalizedLabel].count += count;
          } else {
            mergedDistribution[normalizedLabel] = {
              value: normalizedLabel, // Label
              count: count,
              color: item.color // Keep first color found
            };
          }
          newTotal += count;
        });

        // Convert back to array and recalculate percentages
        const newDistribution = Object.values(mergedDistribution)
          .sort((a, b) => b.count - a.count)
          .map(item => ({
            ...item,
            percentage: newTotal > 0 ? (item.count / newTotal) * 100 : 0
          }));

        normalizedStats[fieldName] = {
          ...fieldData,
          distribution: newDistribution,
          totalResponses: newTotal // Update total if needed, though usually same
        };
      } else {
        // Keep other fields as is
        normalizedStats[fieldName] = fieldData;
      }
    });

    return normalizedStats;
  }, [fieldStatsData]);

  // Determine chart type for each field
  const getChartType = (distribution) => {
    const uniqueValues = distribution.length;
    if (uniqueValues === 0) return 'none';
    if (uniqueValues >= 2 && uniqueValues <= 8) return 'pie';
    if (uniqueValues >= 9 && uniqueValues <= 20) return 'bar';
    return 'table'; // Too many values
  };

  // Export to PDF function
  const handleExportPDF = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    // Show loading message but don't prevent export
    if (isLoading) {
      toast.info('Data is still loading, generating report with available data...');
    }

    // Generate report even if no attendees - show empty report with event info
    if (!attendees || attendees.length === 0) {
      toast.info('No attendees found for this event. Generating empty report...');
    }

    setIsExporting(true);

    try {
      console.log('Starting PDF generation...');
      console.log('Stats:', stats);
      console.log('Attendees count:', attendees.length);
      console.log('Chart data:', chartData);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // Get selected event details
      const selectedEventData = events.find(e => e.id === selectedEvent);
      const eventName = eventDetails?.name || selectedEventData?.name || 'Event';
      const eventLocation = eventDetails?.location || 'N/A';
      const eventDate = eventDetails?.start_date || 'N/A';

      // Professional header design with teal theme
      pdf.setFillColor(20, 184, 166); // Teal-500 (app primary color)
      pdf.rect(0, 0, pageWidth, 55, 'F');

      // Main title - left aligned with proper spacing
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Analytics Report', margin, 25);

      // Event name - left aligned, smaller and elegant
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.text(eventName, margin, 40, { maxWidth: pageWidth - 85 - margin - 5 });

      // Right side information block - properly aligned
      const rightColumnX = pageWidth - 85; // Fixed right column position

      // Generation timestamp
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const formattedTime = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Right-aligned information with consistent spacing
      pdf.text('Generated:', rightColumnX, 20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formattedDate, rightColumnX, 25);
      pdf.text(`at ${formattedTime}`, rightColumnX, 29);

      // Location information
      pdf.setFont('helvetica', 'normal');
      pdf.text('Location:', rightColumnX, 36);
      pdf.setFont('helvetica', 'bold');

      // Handle long location names by truncating if necessary
      let displayLocation = eventLocation;
      if (eventLocation.length > 25) {
        displayLocation = eventLocation.substring(0, 22) + '...';
      }
      pdf.text(displayLocation, rightColumnX, 40);

      // Event date formatting and display
      let eventDateFormatted = eventDate;
      if (eventDate && eventDate !== 'N/A') {
        try {
          const eventDateObj = new Date(eventDate);
          if (!isNaN(eventDateObj.getTime())) {
            eventDateFormatted = eventDateObj.toLocaleDateString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          }
        } catch (e) {
          // Keep original if parsing fails
        }
      }

      pdf.setFont('helvetica', 'normal');
      pdf.text('Event Date:', rightColumnX, 47);
      pdf.setFont('helvetica', 'bold');
      pdf.text(eventDateFormatted, rightColumnX, 51);

      yPosition = 65;

      // Summary Statistics Section
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Summary Statistics', margin, yPosition);
      yPosition += 10;

      // Enhanced stats table
      const totalAttendees = stats.totalAttendees || 0;
      const checkInRate = stats.checkInRate || 0;
      const badgesPrinted = stats.badgesPrinted || 0;
      const checkedInCount = stats.checkedIn || 0;
      const pendingCount = stats.pending || 0;

      const statsData = [
        ['Total Registrations', String(totalAttendees)],
        ['Checked In', String(checkedInCount)],
        ['Pending Check-In', String(pendingCount)],
        ['Check-In Rate', `${parseFloat(checkInRate).toFixed(2)}%`],
        [defaultPrintMode === 'sticker' ? 'Stickers Printed' : 'Badges Printed', String(badgesPrinted)],
      ];


      autoTable(pdf, {
        startY: yPosition,
        head: [['Metric', 'Value']],
        body: statsData,
        theme: 'grid',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 11
        },
        bodyStyles: {
          fontSize: 10,
          textColor: [31, 41, 55]
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        },
        margin: { left: margin, right: margin },
      });

      yPosition = pdf.lastAutoTable.finalY + 15;

      // Key Insights Section (if data available)
      if (genderStats.total > 0 || topProfessions.length > 0 || topLocations.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setTextColor(31, 41, 55);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Key Insights at a Glance', margin, yPosition);
        yPosition += 10;

        const insightsData = [];

        // Gender insight
        if (genderStats.total > 0) {
          const malePercent = ((genderStats.male / genderStats.total) * 100).toFixed(1);
          const femalePercent = ((genderStats.female / genderStats.total) * 100).toFixed(1);
          insightsData.push(['Gender Split', `${genderStats.male} Male (${malePercent}%) | ${genderStats.female} Female (${femalePercent}%)`]);
        }

        // Top profession insight
        if (topProfessions.length > 0) {
          insightsData.push(['Most Common Profession', `${topProfessions[0].name} (${topProfessions[0].value} attendees)`]);
        }

        // Top location insight
        if (topLocations.length > 0) {
          insightsData.push(['Most Common Location', `${topLocations[0].name} (${topLocations[0].value} attendees)`]);
        }

        if (insightsData.length > 0) {
          autoTable(pdf, {
            startY: yPosition,
            body: insightsData,
            theme: 'grid',
            styles: {
              fontSize: 10,
              cellPadding: 5,
            },
            columnStyles: {
              0: {
                cellWidth: 55,
                fontStyle: 'bold',
                fillColor: [243, 244, 246],
                textColor: [31, 41, 55]
              },
              1: {
                textColor: [31, 41, 55]
              }
            },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 15;
        }
      }

      // Add note if no attendees
      if (!attendees || attendees.length === 0) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(239, 68, 68); // Red color
        pdf.text('No Attendee Data Available', margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(107, 114, 128);
        pdf.text('This event currently has no registered attendees.', margin, yPosition);
        yPosition += 6;
        pdf.text('Once attendees register, detailed analytics will be available here.', margin, yPosition);
        yPosition += 15;
      }

      // Check-In Status Breakdown
      if (chartData.checkInStatus.length > 0 && totalAttendees > 0) {
        console.log('Adding check-in status breakdown...');
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Check-In Status Breakdown', margin, yPosition);
        yPosition += 10;

        const checkInData = chartData.checkInStatus.map(item => {
          const percentage = totalAttendees > 0 ? ((item.value / totalAttendees) * 100).toFixed(1) : '0';
          console.log(`Status: ${item.name}, Value: ${item.value}, Percentage: ${percentage}%`);
          return [
            item.name,
            String(item.value),
            `${percentage}%`
          ];
        });

        autoTable(pdf, {
          startY: yPosition,
          head: [['Status', 'Count', 'Percentage']],
          body: checkInData,
          theme: 'grid',
          headStyles: {
            fillColor: [16, 185, 129],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11
          },
          bodyStyles: {
            fontSize: 10,
            textColor: [31, 41, 55]
          },
          alternateRowStyles: {
            fillColor: [249, 250, 251]
          },
          margin: { left: margin, right: margin },
        });

        yPosition = pdf.lastAutoTable.finalY + 15;
      }

      // Demographics Section
      if (Object.keys(demographics).length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Demographics Analysis', margin, yPosition);
        yPosition += 10;

        // Gender demographics summary
        if (genderStats.total > 0) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Gender Distribution Summary', margin, yPosition);
          yPosition += 8;

          const genderSummaryData = [
            ['Male', String(genderStats.male), `${((genderStats.male / genderStats.total) * 100).toFixed(1)}%`],
            ['Female', String(genderStats.female), `${((genderStats.female / genderStats.total) * 100).toFixed(1)}%`],
            ['Total Recorded', String(genderStats.total), '100.0%'],
          ];

          autoTable(pdf, {
            startY: yPosition,
            head: [['Gender', 'Count', 'Percentage']],
            body: genderSummaryData,
            theme: 'grid',
            headStyles: {
              fillColor: [20, 184, 166], // Teal color
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 11
            },
            bodyStyles: {
              fontSize: 10,
              textColor: [31, 41, 55]
            },
            alternateRowStyles: {
              fillColor: [249, 250, 251]
            },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }

        // Full gender demographics (if more than just male/female)
        if (demographics.gender && demographics.gender.length > 2) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Complete Gender Breakdown', margin, yPosition);
          yPosition += 8;

          const genderData = demographics.gender.map(item => [
            item.name,
            String(item.value),
            `${((item.value / totalAttendees) * 100).toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Gender', 'Count', 'Percentage']],
            body: genderData,
            theme: 'grid',
            headStyles: { fillColor: [168, 85, 247], textColor: [255, 255, 255] },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }

        // Age groups
        if (demographics.ageGroups && demographics.ageGroups.length > 0) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Age Distribution', margin, yPosition);
          yPosition += 8;

          const ageData = demographics.ageGroups.map(item => [
            item.name,
            String(item.value),
            `${((item.value / totalAttendees) * 100).toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Age Group', 'Count', 'Percentage']],
            body: ageData,
            theme: 'grid',
            headStyles: { fillColor: [245, 158, 11], textColor: [255, 255, 255] },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }

        // Top Professional Categories (Highlighted)
        if (topProfessions.length > 0) {
          // Check if we need a new page
          if (yPosition > pageHeight - 80) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Top 3 Professional Categories', margin, yPosition);
          yPosition += 8;

          const topProfData = topProfessions.map((item, index) => [
            `${index + 1}`,
            item.name,
            String(item.value),
            `${((item.value / totalAttendees) * 100).toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Rank', 'Category', 'Count', 'Percentage']],
            body: topProfData,
            theme: 'grid',
            headStyles: {
              fillColor: [168, 85, 247], // Purple color
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 11
            },
            bodyStyles: {
              fontSize: 10,
              textColor: [31, 41, 55]
            },
            alternateRowStyles: {
              fillColor: [249, 250, 251]
            },
            columnStyles: {
              0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' }
            },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }

        // All Professional categories
        if (demographics.professionalCategories && demographics.professionalCategories.length > 0) {
          // Check if we need a new page
          if (yPosition > pageHeight - 80) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('All Professional Categories', margin, yPosition);
          yPosition += 8;

          const profData = demographics.professionalCategories.map(item => [
            item.name,
            String(item.value),
            `${((item.value / totalAttendees) * 100).toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Category', 'Count', 'Percentage']],
            body: profData,
            theme: 'grid',
            headStyles: { fillColor: [34, 197, 94], textColor: [255, 255, 255] },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }
      }

      // Geographic Analysis
      if (Object.keys(geographic).length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 80) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Geographic Distribution', margin, yPosition);
        yPosition += 10;

        // Top 3 Locations (Highlighted)
        if (topLocations.length > 0) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Top 3 Locations', margin, yPosition);
          yPosition += 8;

          const topLocData = topLocations.map((item, index) => [
            `${index + 1}`,
            item.name,
            String(item.value),
            `${((item.value / totalAttendees) * 100).toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Rank', 'Location', 'Count', 'Percentage']],
            body: topLocData,
            theme: 'grid',
            headStyles: {
              fillColor: [249, 115, 22], // Orange color
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 11
            },
            bodyStyles: {
              fontSize: 10,
              textColor: [31, 41, 55]
            },
            alternateRowStyles: {
              fillColor: [249, 250, 251]
            },
            columnStyles: {
              0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' }
            },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }

        // All Geographic Data
        Object.entries(geographic).forEach(([fieldName, data]) => {
          if (data && data.length > 0) {
            // Check if we need a new page
            if (yPosition > pageHeight - 80) {
              pdf.addPage();
              yPosition = margin;
            }

            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`All ${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} Data`, margin, yPosition);
            yPosition += 8;

            const geoData = data.slice(0, 10).map(item => [
              item.name,
              String(item.value),
              `${((item.value / totalAttendees) * 100).toFixed(1)}%`
            ]);

            autoTable(pdf, {
              startY: yPosition,
              head: [[fieldName.charAt(0).toUpperCase() + fieldName.slice(1), 'Count', 'Percentage']],
              body: geoData,
              theme: 'grid',
              headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255] },
              margin: { left: margin, right: margin },
            });

            yPosition = pdf.lastAutoTable.finalY + 10;
          }
        });
      }

      // Registration Trend Data
      if (chartData.registrationTrend.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Registration Trend (Last 7 Days)', margin, yPosition);
        yPosition += 10;

        // Capture Registration Trend Chart
        try {
          const trendChartElement = document.querySelector('#registration-trend-chart');
          if (trendChartElement) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(trendChartElement, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true,
              removeContainer: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing registration trend chart:', error);
          // Fallback to table if chart capture fails
          const trendData = chartData.registrationTrend.map(item => [
            item.date,
            String(item.registrations)
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Date', 'Registrations']],
            body: trendData,
            theme: 'grid',
            headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
            margin: { left: margin, right: margin },
          });
          yPosition = pdf.lastAutoTable.finalY + 15;
        }
      }

      // Time Analytics Summary (Peak Hours)
      if (timeAnalytics.hourly && timeAnalytics.hourly.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Peak Registration Hours', margin, yPosition);
        yPosition += 10;

        // Capture Peak Hours Chart
        try {
          const peakChartElement = document.querySelector('#peak-hours-chart');
          if (peakChartElement) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(peakChartElement, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true,
              removeContainer: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing peak hours chart:', error);
          // Fallback to table
          const peakHours = timeAnalytics.hourly
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
            .map(item => {
              let timeLabel = item.label;
              if (timeLabel && timeLabel.includes(':')) {
                const [hours, minutes] = timeLabel.split(':');
                const hour24 = parseInt(hours, 10);
                const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                const ampm = hour24 >= 12 ? 'PM' : 'AM';
                timeLabel = `${hour12}:${minutes} ${ampm}`;
              }
              return [timeLabel, String(item.count)];
            });

          if (peakHours.length > 0) {
            autoTable(pdf, {
              startY: yPosition,
              head: [['Time', 'Registrations']],
              body: peakHours,
              theme: 'grid',
              headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
              margin: { left: margin, right: margin },
            });
            yPosition = pdf.lastAutoTable.finalY + 15;
          }
        }
      }

      // Add Check-In Status Pie Chart
      if (chartData.checkInStatus.length > 0 && totalAttendees > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 200) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Check-In Status Chart', margin, yPosition);
        yPosition += 10;

        // Try to capture check-in status chart
        try {
          const checkInChartElement = document.querySelector('#check-in-status-chart');
          if (checkInChartElement) {
            // Wait a bit for chart to fully render
            await new Promise(resolve => setTimeout(resolve, 300));

            const canvas = await html2canvas(checkInChartElement, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true
            });

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add chart image
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing check-in status chart:', error);
        }
      }

      // Demographic Charts Section
      console.log('Adding demographic charts to PDF...');

      // Gender Pie Chart
      if (demographics.gender && demographics.gender.length > 0) {
        if (yPosition > pageHeight - 200) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Gender Distribution (Pie Chart)', margin, yPosition);
        yPosition += 10;

        try {
          const genderPieChart = document.querySelector('#gender-pie-chart');
          if (genderPieChart) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(genderPieChart, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing gender pie chart:', error);
        }
      }

      // Gender Bar Chart
      if (demographics.gender && demographics.gender.length > 0) {
        if (yPosition > pageHeight - 200) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Gender Distribution (Bar Chart)', margin, yPosition);
        yPosition += 10;

        try {
          const genderBarChart = document.querySelector('#gender-bar-chart');
          if (genderBarChart) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(genderBarChart, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing gender bar chart:', error);
        }
      }

      // Professions Pie Chart
      if (demographics.professionalCategories && demographics.professionalCategories.length > 0) {
        if (yPosition > pageHeight - 200) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Professional Categories', margin, yPosition);
        yPosition += 10;

        try {
          const professionsChart = document.querySelector('#professions-pie-chart');
          if (professionsChart) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(professionsChart, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing professions chart:', error);
        }
      }

      // Locations Pie Chart
      if (demographics.geographicDistribution && demographics.geographicDistribution.length > 0) {
        if (yPosition > pageHeight - 200) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Geographic Distribution', margin, yPosition);
        yPosition += 10;

        try {
          const locationsChart = document.querySelector('#locations-pie-chart');
          if (locationsChart) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(locationsChart, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing locations chart:', error);
        }
      }

      // Age Groups Bar Chart
      if (demographics.ageGroups && demographics.ageGroups.length > 0) {
        if (yPosition > pageHeight - 200) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Age Distribution', margin, yPosition);
        yPosition += 10;

        try {
          const ageChart = document.querySelector('#age-bar-chart');
          if (ageChart) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const canvas = await html2canvas(ageChart, {
              backgroundColor: 'white',
              scale: 2,
              logging: false,
              useCORS: true
            });
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 15;
          }
        } catch (error) {
          console.error('Error capturing age chart:', error);
        }
      }

      console.log('Demographic charts added to PDF');

      // Field Statistics Charts Section
      if (fieldStatistics && Object.keys(fieldStatistics).length > 0) {
        console.log('Adding field statistics charts to PDF...');

        for (const [fieldName, fieldData] of Object.entries(fieldStatistics)) {
          if (fieldData.distribution.length === 0) continue;

          // Check if we need a new page
          if (yPosition > pageHeight - 200) {
            pdf.addPage();
            yPosition = margin;
          }

          // Section title
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(31, 41, 55);
          pdf.text(`${fieldData.fieldLabel} Distribution`, margin, yPosition);
          yPosition += 10;

          // Capture chart as image
          try {
            const chartElement = document.querySelector(`#chart-${fieldName}`);
            if (chartElement) {
              // Wait a bit for chart to fully render
              await new Promise(resolve => setTimeout(resolve, 300));

              const canvas = await html2canvas(chartElement, {
                backgroundColor: 'white',
                scale: 2,
                logging: false,
                useCORS: true,
                removeContainer: true
              });

              const imgData = canvas.toDataURL('image/png');
              const imgWidth = pageWidth - (margin * 2);
              const imgHeight = (canvas.height * imgWidth) / canvas.width;

              // Add chart image
              pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
              yPosition += imgHeight + 10;
            } else {
              console.warn(`Chart element not found for field: ${fieldName}`);
            }
          } catch (error) {
            console.error(`Error capturing chart for ${fieldName}:`, error);
          }

          // Add data table below chart
          const tableData = fieldData.distribution.slice(0, 10).map(item => [
            item.value,
            String(item.count),
            `${item.percentage.toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: yPosition,
            head: [['Value', 'Count', 'Percentage']],
            body: tableData,
            theme: 'grid',
            headStyles: {
              fillColor: [59, 130, 246],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 11
            },
            bodyStyles: {
              fontSize: 10,
              textColor: [31, 41, 55]
            },
            alternateRowStyles: {
              fillColor: [249, 250, 251]
            },
            margin: { left: margin, right: margin },
          });

          yPosition = pdf.lastAutoTable.finalY + 20;
        }

        console.log('Field statistics charts added to PDF');
      }

      // Footer on last page
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.text(
          `Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        pdf.text(
          'Generated by Event Management System',
          margin,
          pageHeight - 10
        );
      }

      // Save the PDF
      const safeEventName = (eventName || 'Event').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${safeEventName}_Comprehensive_Analytics_${dateStr}.pdf`;
      console.log('Saving PDF with filename:', fileName);
      pdf.save(fileName);

      console.log('PDF generated successfully!');
      toast.success('PDF report generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error stack:', error.stack);
      toast.error(`Failed to generate PDF report: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Export to CSV function (Raw Data)
  const handleExportCSV = async () => {
    if (!selectedEvent) return;

    try {
      setIsExporting(true);
      toast.info('Generating CSV export...');

      const response = await attendeeAPI.export(selectedEvent);

      // Create download link for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `attendees_export_${new Date().toISOString().split('T')[0]}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('CSV export completed successfully');
    } catch (error) {
      console.error('CSV Export Error:', error);
      toast.error('Failed to export CSV data');
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate gender distribution
  const genderStats = useMemo(() => {
    // First try to get from demographics API
    if (demographics.gender && demographics.gender.length > 0) {
      const maleData = demographics.gender.find(g => g.name && g.name.toLowerCase() === 'male');
      const femaleData = demographics.gender.find(g => g.name && g.name.toLowerCase() === 'female');

      const maleCount = maleData?.value || 0;
      const femaleCount = femaleData?.value || 0;

      // Only return if we have actual data
      if (maleCount > 0 || femaleCount > 0) {
        return {
          male: maleCount,
          female: femaleCount,
          total: maleCount + femaleCount
        };
      }
    }

    // Fallback: Calculate from attendees data directly
    if (attendees && attendees.length > 0) {
      const genderCounts = attendees.reduce((acc, attendee) => {
        // Check different possible gender field locations
        const genderValue = attendee.gender ||
          attendee.data?.find(d => d.field?.field_name === 'gender')?.value ||
          attendee.data?.find(d => d.field?.field_name === 'gender')?.field_value;

        if (genderValue) {
          const normalizedGender = genderValue.toLowerCase();
          if (normalizedGender === 'male') acc.male++;
          else if (normalizedGender === 'female') acc.female++;
        }
        return acc;
      }, { male: 0, female: 0 });

      return {
        male: genderCounts.male,
        female: genderCounts.female,
        total: genderCounts.male + genderCounts.female
      };
    }

    return { male: 0, female: 0, total: 0 };
  }, [demographics, attendees]);

  // Calculate top professional categories
  const topProfessions = useMemo(() => {
    // First try from demographics API
    if (demographics.professionalCategories && demographics.professionalCategories.length > 0) {
      return demographics.professionalCategories
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);
    }

    // Fallback: Calculate from attendees data
    if (attendees && attendees.length > 0) {
      const professionCounts = {};
      attendees.forEach(attendee => {
        const profession = attendee.profession ||
          attendee.position ||
          attendee.data?.find(d => d.field?.field_name === 'profession')?.value ||
          attendee.data?.find(d => d.field?.field_name === 'position')?.value;

        if (profession && profession.trim()) {
          professionCounts[profession] = (professionCounts[profession] || 0) + 1;
        }
      });

      const professionArray = Object.entries(professionCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);

      return professionArray;
    }

    return [];
  }, [demographics, attendees]);

  // Calculate top locations
  const topLocations = useMemo(() => {
    // First try from geographic API
    if (geographic.city && geographic.city.length > 0) {
      return geographic.city
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);
    }

    // Fallback: Calculate from attendees data
    if (attendees && attendees.length > 0) {
      const locationCounts = {};
      attendees.forEach(attendee => {
        const location = attendee.city ||
          attendee.location ||
          attendee.data?.find(d => d.field?.field_name === 'city')?.value ||
          attendee.data?.find(d => d.field?.field_name === 'location')?.value;

        if (location && location.trim()) {
          locationCounts[location] = (locationCounts[location] || 0) + 1;
        }
      });

      const locationArray = Object.entries(locationCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);

      return locationArray;
    }

    return [];
  }, [geographic, attendees]);

  const statCards = [
    {
      label: 'Total Registrations',
      value: stats.totalAttendees || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      label: 'Check-In Rate',
      value: `${stats.checkInRate || 0}%`,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      label: defaultPrintMode === 'sticker' ? 'Stickers Printed' : 'Badges Printed',
      value: stats.badgesPrinted || 0,
      icon: Printer,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      label: 'Growth',
      value: stats.growthRate ? `${stats.growthRate > 0 ? '+' : ''}${stats.growthRate}%` : '0%',
      icon: TrendingUp,
      color: stats.growthRate && stats.growthRate >= 0 ? 'text-orange-600' : 'text-red-600',
      bgColor: stats.growthRate && stats.growthRate >= 0 ? 'bg-orange-100' : 'bg-red-100',
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BarChart3 className="h-8 w-8 text-blue-600" />
                Analytics & Reports
              </h1>
              <p className="text-gray-600 mt-2">Comprehensive insights and detailed reporting for your events</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleExportPDF}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                disabled={isExporting || !selectedEvent}
                loading={isExporting}
              >
                <FileText className="h-5 w-5" />
                {isExporting ? 'Generating PDF...' : 'Export PDF Report'}
              </Button>
              <Button
                onClick={handleExportCSV}
                className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                disabled={isExporting || !selectedEvent}
                loading={isExporting}
              >
                <FileText className="h-5 w-5" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Event Selector */}
        <Card className="shadow-sm border-gray-200">
          <Card.Content className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">Select Event</h3>
            </div>
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
            >
              <option value="">Choose an event to analyze...</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} {event.start_date && `• ${new Date(event.start_date).toLocaleDateString()}`}
                </option>
              ))}
            </select>
          </Card.Content>
        </Card>

        {/* Stats Grid */}
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading analytics...</div>
        ) : selectedEvent ? (
          <>
            {/* Stats Overview */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="h-6 w-6 text-blue-600" />
                Key Metrics Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-200 border-gray-200">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                          <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                        </div>
                        <div className={`p-3 rounded-xl ${stat.bgColor} flex-shrink-0`}>
                          <stat.icon className={`h-7 w-7 ${stat.color}`} />
                        </div>
                      </div>
                    </Card.Content>
                  </Card>
                ))}

                {/* Gender Distribution Card */}
                <Card className="hover:shadow-lg transition-shadow duration-200 border-gray-200">
                  <Card.Content className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-600">Gender Distribution</p>
                      <div className="p-3 rounded-xl bg-teal-100 flex-shrink-0">
                        <UserCheck className="h-7 w-7 text-teal-600" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate(`/attendees?gender=Male&event=${selectedEvent}`)}
                        className="w-full flex items-center justify-between hover:bg-blue-50 p-2 rounded-lg transition-colors group"
                        title="Click to view male attendees"
                      >
                        <span className="text-sm text-gray-700 font-medium group-hover:text-blue-700">Male:</span>
                        <span className="text-xl font-bold text-blue-600 group-hover:scale-110 transition-transform">{genderStats.male}</span>
                      </button>
                      <button
                        onClick={() => navigate(`/attendees?gender=Female&event=${selectedEvent}`)}
                        className="w-full flex items-center justify-between hover:bg-pink-50 p-2 rounded-lg transition-colors group"
                        title="Click to view female attendees"
                      >
                        <span className="text-sm text-gray-700 font-medium group-hover:text-pink-700">Female:</span>
                        <span className="text-xl font-bold text-pink-600 group-hover:scale-110 transition-transform">{genderStats.female}</span>
                      </button>
                      {genderStats.total > 0 && (
                        <div className="pt-2 border-t border-gray-200">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Total Recorded:</span>
                            <span className="font-semibold">{genderStats.total}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card.Content>
                </Card>
              </div>
            </div>

            {/* Additional Data Groupings */}
            {(topProfessions.length > 0 || topLocations.length > 0) && (
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                  Top Categories
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Top Professional Categories */}
                  {topProfessions.length > 0 && (
                    <Card className="hover:shadow-lg transition-shadow duration-200 border-gray-200">
                      <Card.Content className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-base font-semibold text-gray-900">Top Professions</h3>
                          <div className="p-2 rounded-lg bg-purple-100">
                            <Briefcase className="h-5 w-5 text-purple-600" />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {topProfessions.map((profession, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-bold flex items-center justify-center">
                                  {index + 1}
                                </span>
                                <span className="text-sm text-gray-700 truncate">{profession.name}</span>
                              </div>
                              <span className="text-lg font-bold text-purple-600 ml-2">{profession.value}</span>
                            </div>
                          ))}
                        </div>
                      </Card.Content>
                    </Card>
                  )}

                  {/* Top Locations */}
                  {topLocations.length > 0 && (
                    <Card className="hover:shadow-lg transition-shadow duration-200 border-gray-200">
                      <Card.Content className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-base font-semibold text-gray-900">Top Locations</h3>
                          <div className="p-2 rounded-lg bg-orange-100">
                            <MapPin className="h-5 w-5 text-orange-600" />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {topLocations.map((location, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center">
                                  {index + 1}
                                </span>
                                <span className="text-sm text-gray-700 truncate">{location.name}</span>
                              </div>
                              <span className="text-lg font-bold text-orange-600 ml-2">{location.value}</span>
                            </div>
                          ))}
                        </div>
                      </Card.Content>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {/* Charts Section */}
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <PieChartIcon className="h-6 w-6 text-blue-600" />
                Data Visualization
              </h2>

              <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Registration Trend - Area Chart */}
                <Card className="chart-container shadow-sm border-gray-200" id="registration-trend-chart">
                  <Card.Content className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Registration Trend</h3>
                        <p className="text-sm text-gray-500 mt-1">Daily registration activity over time</p>
                      </div>
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    {chartData.registrationTrend.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={chartData.registrationTrend}>
                          <defs>
                            <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" stroke="#6B7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                            labelStyle={{ fontWeight: 'bold', color: '#111827' }}
                          />
                          <Area
                            type="monotone"
                            dataKey="registrations"
                            stroke="#3B82F6"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorRegistrations)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-gray-400">
                        No registration data available
                      </div>
                    )}
                  </Card.Content>
                </Card>

                {/* Peak Registration Hours - Bar Chart */}
                {chartData.hourlyDistribution && chartData.hourlyDistribution.length > 0 && (
                  <Card className="chart-container shadow-sm border-gray-200" id="peak-hours-chart">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Peak Registration Hours</h3>
                          <p className="text-sm text-gray-500 mt-1">Busiest times for registration</p>
                        </div>
                        <Clock className="h-5 w-5 text-gray-400" />
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={chartData.hourlyDistribution
                            .sort((a, b) => {
                              // Sort by time label if possible, otherwise by count
                              return a.label.localeCompare(b.label);
                            })
                            .map(item => {
                              // Format label to 12h
                              let timeLabel = item.label;
                              if (timeLabel && timeLabel.includes(':')) {
                                const [hours] = timeLabel.split(':');
                                const hour24 = parseInt(hours, 10);
                                const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                                const ampm = hour24 >= 12 ? 'PM' : 'AM';
                                timeLabel = `${hour12} ${ampm}`;
                              }
                              return { ...item, displayLabel: timeLabel };
                            })
                          }
                          margin={{ top: 5, right: 20, left: 10, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="displayLabel" stroke="#6B7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                            cursor={{ fill: '#F3F4F6' }}
                          />
                          <Bar dataKey="count" name="Registrations" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Content>
                  </Card>
                )}

                {/* Check-In Status - Pie Chart */}
                <Card className="chart-container shadow-sm border-gray-200" id="check-in-status-chart">
                  <Card.Content className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Check-In Status</h3>
                        <p className="text-sm text-gray-500 mt-1">Current attendee check-in distribution</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-gray-400" />
                    </div>
                    {chartData.checkInStatus.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={chartData.checkInStatus}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {chartData.checkInStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-gray-400">
                        No check-in data available
                      </div>
                    )}
                  </Card.Content>
                </Card>

                {/* Gender Distribution - Pie Chart */}
                {demographics.gender && demographics.gender.length > 0 && (
                  <Card className="chart-container shadow-sm border-gray-200" id="gender-pie-chart">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Gender Distribution (Pie)</h3>
                          <p className="text-sm text-gray-500 mt-1">Attendee gender breakdown</p>
                        </div>
                        <Users className="h-5 w-5 text-gray-400" />
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={demographics.gender.map((item, index) => ({
                              name: item.name,
                              value: item.value,
                              color: ['#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'][index % 5]
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {demographics.gender.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'][index % 5]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Content>
                  </Card>
                )}

                {/* Gender Distribution - Bar Chart */}
                {demographics.gender && demographics.gender.length > 0 && (
                  <Card className="chart-container shadow-sm border-gray-200" id="gender-bar-chart">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Gender Distribution (Bar)</h3>
                          <p className="text-sm text-gray-500 mt-1">Attendee count by gender</p>
                        </div>
                        <Users className="h-5 w-5 text-gray-400" />
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={demographics.gender.map((item, index) => ({
                            name: item.name,
                            count: item.value,
                            color: ['#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'][index % 5]
                          }))}
                          margin={{ top: 5, right: 20, left: 10, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis
                            dataKey="name"
                            stroke="#6B7280"
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          />
                          <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                            {demographics.gender.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'][index % 5]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Content>
                  </Card>
                )}

                {/* Professional Categories - Pie Chart */}
                {demographics.professionalCategories && demographics.professionalCategories.length > 0 && (
                  <Card className="chart-container shadow-sm border-gray-200" id="professions-pie-chart">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Professional Categories</h3>
                          <p className="text-sm text-gray-500 mt-1">Top professions distribution</p>
                        </div>
                        <Briefcase className="h-5 w-5 text-gray-400" />
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={demographics.professionalCategories.slice(0, 8).map((item, index) => ({
                              name: item.name,
                              value: item.value,
                              color: ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316'][index % 8]
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => percent > 0.05 ? `${name.substring(0, 15)}: ${(percent * 100).toFixed(0)}%` : ''}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {demographics.professionalCategories.slice(0, 8).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316'][index % 8]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            formatter={(value) => value.length > 20 ? value.substring(0, 18) + '...' : value}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Content>
                  </Card>
                )}

                {/* Locations - Pie Chart */}
                {demographics.geographicDistribution && demographics.geographicDistribution.length > 0 && (
                  <Card className="chart-container shadow-sm border-gray-200" id="locations-pie-chart">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Geographic Distribution</h3>
                          <p className="text-sm text-gray-500 mt-1">Attendee locations</p>
                        </div>
                        <MapPin className="h-5 w-5 text-gray-400" />
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={demographics.geographicDistribution.slice(0, 8).map((item, index) => ({
                              name: item.name,
                              value: item.value,
                              color: ['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'][index % 8]
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => percent > 0.05 ? `${name.substring(0, 12)}: ${(percent * 100).toFixed(0)}%` : ''}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {demographics.geographicDistribution.slice(0, 8).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'][index % 8]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            formatter={(value) => value.length > 20 ? value.substring(0, 18) + '...' : value}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Content>
                  </Card>
                )}

                {/* Age Groups - Bar Chart */}
                {demographics.ageGroups && demographics.ageGroups.length > 0 && (
                  <Card className="chart-container shadow-sm border-gray-200" id="age-bar-chart">
                    <Card.Content className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Age Distribution</h3>
                          <p className="text-sm text-gray-500 mt-1">Attendee age groups</p>
                        </div>
                        <Users className="h-5 w-5 text-gray-400" />
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={demographics.ageGroups.map((item, index) => ({
                            name: item.name,
                            count: item.value,
                            color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]
                          }))}
                          margin={{ top: 5, right: 20, left: 10, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis
                            dataKey="name"
                            stroke="#6B7280"
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                          />
                          <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                            {demographics.ageGroups.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Content>
                  </Card>
                )}
              </div>
            </div>

            {/* Field Statistics Charts Section */}
            {!fieldStatsLoading && fieldStatistics && Object.keys(fieldStatistics).length > 0 && (
              <div className="space-y-6 mt-8">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                  Field-by-Field Analysis ({Object.keys(fieldStatistics).length} fields)
                </h2>
                <p className="text-sm text-gray-600 -mt-4">
                  Comprehensive analysis of all custom fields for this event
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {Object.entries(fieldStatistics).map(([fieldName, fieldData]) => {
                    const chartType = getChartType(fieldData.distribution);

                    if (chartType === 'pie') {
                      return (
                        <PieChartCard
                          key={fieldName}
                          title={fieldData.fieldLabel}
                          data={fieldData.distribution}
                          total={fieldData.totalResponses}
                          fieldName={fieldName}
                        />
                      );
                    }

                    if (chartType === 'bar') {
                      return (
                        <BarChartCard
                          key={fieldName}
                          title={fieldData.fieldLabel}
                          data={fieldData.distribution}
                          total={fieldData.totalResponses}
                          fieldName={fieldName}
                        />
                      );
                    }

                    // For 'table' or 'none' types, show a summary card
                    if (chartType === 'table' && fieldData.distribution.length > 0) {
                      return (
                        <Card key={fieldName} className="shadow-sm border-gray-200">
                          <Card.Content className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                              {fieldData.fieldLabel}
                            </h3>
                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                              {fieldData.distribution.slice(0, 20).map((item, index) => (
                                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                  <span className="text-sm text-gray-700 truncate flex-1 pr-4">
                                    {item.value}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {item.count}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      ({item.percentage.toFixed(1)}%)
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">Total responses:</span>
                                <span className="font-bold text-gray-900">{fieldData.totalResponses}</span>
                              </div>
                              <div className="flex justify-between items-center text-sm mt-1">
                                <span className="text-gray-600">Response rate:</span>
                                <span className="font-bold text-gray-900">{fieldData.responseRate}</span>
                              </div>
                              {fieldData.distribution.length > 20 && (
                                <p className="text-xs text-gray-500 mt-2">
                                  Showing top 20 of {fieldData.distribution.length} values
                                </p>
                              )}
                            </div>
                          </Card.Content>
                        </Card>
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            )}

            {/* Loading state for field statistics */}
            {fieldStatsLoading && (
              <div className="mt-8 text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-4">Loading field statistics...</p>
              </div>
            )}

            {/* Empty state for field statistics */}
            {!fieldStatsLoading && fieldStatistics && Object.keys(fieldStatistics).length === 0 && (
              <div className="mt-8">
                <Card>
                  <Card.Content className="text-center py-12">
                    <p className="text-gray-500">No analyzable custom fields found for this event</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Add custom fields like Gender, Profession, or City to see detailed analytics
                    </p>
                  </Card.Content>
                </Card>
              </div>
            )}
          </>
        ) : (
          <Card>
            <Card.Content className="text-center py-12">
              <p className="text-gray-500">Select an event to view analytics</p>
            </Card.Content>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
